import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { StageActions } from 'src/app/modules/dashboard/interface/actions-response';
import { IEPResponse } from 'src/app/modules/dashboard/interface/dashboard-response';
import { EligibilityResponse } from 'src/app/modules/dashboard/interface/eligibility-response';
import { IAssessmentPlanning } from 'src/app/modules/evaluations/interfaces/assessment-planning';
import { CertificationRequest } from 'src/app/modules/evaluations/interfaces/need-more-information';
import { EvalSubmit } from 'src/app/modules/evaluations/models/assessment-planning/assessment-planning-save.model';
import { EvaluationService } from 'src/app/modules/evaluations/services/evaluation.service';
import { StudentService } from 'src/app/services/student.service';
import { handleError } from 'src/app/shared/functions/error-handler';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { CapatureFirstAttendResponse } from 'src/app/shared/interfaces/capture-first-attend';
import { FirstAttendDateRequest } from 'src/app/shared/interfaces/first-attend-date';
import { ISedmsFormOutput } from 'src/app/shared/interfaces/form/sedms-form-output.model';
import {
  Attendees,
  IMeetingDetails,
} from 'src/app/shared/interfaces/meeting-details';
import { StudentProfile } from 'src/app/shared/interfaces/student';
import { StudentDetails } from 'src/app/shared/interfaces/student-details';
import { AppConfigService } from '../../../../services/app-config.service';
import { IAppConfig } from '../../../../shared/interfaces/app-config';
import { ICalculatedEndDateRequest } from '../interfaces/calculated-end-date';
import { CBSTRequest } from '../interfaces/cbst';
import { Compliance } from '../interfaces/compliance';
import { EducationalBenefitReviewResponse } from '../interfaces/educational-benefit-review';
import { EligibilityRequest } from '../interfaces/eligibility';
import { IepMeeting } from '../interfaces/iep-meeting';
import { IEPProgramServicesKeyValuePairModel } from '../interfaces/iep-program-service';
import { IIepContributor } from '../interfaces/iep-contributor';
import { IIepSectionContributor } from '../interfaces/iep-section-contributor';
import { IepTeam } from '../interfaces/iep-team';
import { IIepType } from '../interfaces/iep-type';
import { ILock } from '../interfaces/lock';
import { ILockRequest } from '../interfaces/lock-request';
import { PreparerInformation } from '../interfaces/lre-form';
import { IepDetailsResponse } from '../interfaces/previous-iep-details';
import {
  IepProgressReport,
  ProgressReportDetail,
  ProgressReportRequest,
} from '../interfaces/progress-report';
import { SectionLevelStatus } from '../interfaces/section-level-status';
import { SpecialTransportationRequest } from '../interfaces/special-transportation';
import { AttendancePageRequest } from '../models/attendance-page-models/attendance-page-request-model';
import { PrintProgressReportModel } from '../models/iep-progress-to-parents-models/iep-progress-print-model';
import { AddProgramServicesRequest } from '../models/iep-recommended-program-and-service-models/add-program-services.model';
import { assertTrustedUrl } from 'src/app/shared/functions/validate-url';
import { Assessment } from '../interfaces/assessment-checklist';

@Injectable({
  providedIn: 'root',
})
export class IEPService<T extends ISedmsFormOutput> {
  config: IAppConfig;
  iepType: Subject<IIepType> = new Subject<IIepType>();
  private iepMeeting: BehaviorSubject<IepMeeting> =
    new BehaviorSubject<IepMeeting>(null);
  public iepMeeting$: Observable<IepMeeting> = this.iepMeeting.asObservable();
  iepSave: Subject<string> = new Subject<string>();
  private contributors: BehaviorSubject<IIepSectionContributor[]> =
    new BehaviorSubject<IIepSectionContributor[]>([]);
  public contributors$: Observable<IIepSectionContributor[]> =
    this.contributors.asObservable();

  public componentLoaded: BehaviorSubject<FormGroup> =
    new BehaviorSubject<FormGroup>(null);
  componentLoadedData$: Observable<FormGroup> =
    this.componentLoaded.asObservable();

  private eligibilityStatusSubject: BehaviorSubject<number | null> =
    new BehaviorSubject<number | null>(null);
  eligibilityStatus$: Observable<number | null> =
    this.eligibilityStatusSubject.asObservable();

  public selectedDisabilitySubject: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  selectedDisability$: Observable<number | null> = this.selectedDisabilitySubject.asObservable();
  public selectedStudent: StudentProfile | StudentDetails;
  selectedStudentSubject = new BehaviorSubject<StudentProfile | StudentDetails | null>(null);
  selectedStudent$ = this.selectedStudentSubject.asObservable();  
  public previousSectionStatus: SectionLevelStatus[];

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private evaluationService: EvaluationService<EvalSubmit>,
    private studentService: StudentService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  private trustedApiUrl(path: string): string {
    return assertTrustedUrl(`${this.config.apiUrl}${path}`, this.config.apiUrl);
  }

  private trustedStudentApiUrl(path: string): string {
    return assertTrustedUrl(
      `${this.config.studentApiUrl}${path}`,
      this.config.studentApiUrl
    );
  }

  private sanitizePathSegment(value: string | number): string {
    return encodeURIComponent(String(value ?? '').replace(/[^A-Za-z0-9_-]/g, ''));
  }

  private assertSafeIdentifier(value: string | number): string {
    const candidate: string = String(value ?? '').trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(candidate)) {
      throw new Error('Invalid identifier');
    }
    return candidate;
  }

  // TODO: Requires a lot of changes to strongly type as this is used in a LOT of components, leaving as any for now.

  public submit(request: T, type: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/Add`,
      this.config.apiUrl
    );
    return this.http
      .post(url, request)
      .pipe(catchError(handleError));
  }

  public submitAssuranceCheckList(request: T, type: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/AddAssuranceWorklist`,
      this.config.apiUrl
    );
    return this.http
      .post(url, request)
      .pipe(catchError(handleError));
  }
  public submitLreForm(request: T, type: string): Observable<any> {
    const url = this.trustedApiUrl('/CBST/SubmitLRE');
    return this.http
      .post(url, request)
      .pipe(catchError(handleError));
  }
  public SubmitAssuranceChecklistForm(request: T, type: string): Observable<any> {
    const url = this.trustedApiUrl('/CBST/SubmitAssuranceChecklist');
    return this.http
      .post(url, request)
      .pipe(catchError(handleError));
  }
  public submitCentralPackageReview(request: CertificationRequest, type: string): Observable<any> {
    const url = this.trustedApiUrl('/CBST/CentralPackageReview');
    return this.http
      .post(url, request)
      .pipe(catchError(handleError));
  }
  public submitCbstApproval(request: T, type: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/CBSTAprroval`,
      this.config.apiUrl
    );
    return this.http
      .post(url, request)
      .pipe(catchError(handleError));
  }
  public amend(
    request: T,
    type: string
  ): Observable<ApiResponse<EligibilityRequest>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/Amend`,
      this.config.apiUrl
    );
    return this.http
      .post<ApiResponse<EligibilityRequest>>(url, request)
      .pipe(catchError(handleError));
  }
  public submitMeeting(
    request: T,
    type: string
  ): Observable<ApiResponse<IMeetingDetails>> {
    const url = assertTrustedUrl(
      `${this.config.studentApiUrl}/${encodeURIComponent(type)}/Add`,
      this.config.studentApiUrl
    );
    return this.http
      .post<ApiResponse<IMeetingDetails>>(url, request)
      .pipe(catchError(handleError));
  }
  public updateMeeting(
    request: T,
    type: string
  ): Observable<ApiResponse<IMeetingDetails>> {
    const url = assertTrustedUrl(
      `${this.config.studentApiUrl}/${encodeURIComponent(type)}/Update`,
      this.config.studentApiUrl
    );
    return this.http
      .post<ApiResponse<IMeetingDetails>>(url, request)
      .pipe(catchError(handleError));
  }
  public updateMeetingAttendees(
    request: T,
    type: string
  ): Observable<AttendancePageRequest> {
    const url = assertTrustedUrl(
      `${this.config.studentApiUrl}/${encodeURIComponent(type)}/UpdateAttendees`,
      this.config.studentApiUrl
    );
    return this.http
      .post<AttendancePageRequest>(url, request)
      .pipe(catchError(handleError));
  }

  public loadAllContributors(iepFormSk: string): void {
    this.http
      .get<IIepSectionContributor[]>(
        assertTrustedUrl(`${this.config.apiUrl}/Contributor/GetIEPContributors/${encodeURIComponent(iepFormSk)}`, this.config.apiUrl)
      )
      .pipe(catchError(handleError))
      .subscribe((data: IIepSectionContributor[]) => {
        this.contributors.next(data);
      });
  }

  public loadAllProgressReportContributors(
    progressReportSk: string,
    mandateSK: string,
    annualGoalSeq?: number
  ): Observable<IIepContributor[]> {
    const encodedProgressReportSk = encodeURIComponent(progressReportSk);
    const encodedAnnualGoalSeq = encodeURIComponent(String(annualGoalSeq ?? ''));
    const endpoint: string = mandateSK
      ? `${this.config.apiUrl}/ProgressReport/GetContributors/${encodedProgressReportSk}/${encodedAnnualGoalSeq}?mandateSk=${encodeURIComponent(mandateSK)}`
      : `${this.config.apiUrl}/ProgressReport/GetContributors/${encodedProgressReportSk}/${encodedAnnualGoalSeq}`;
    const url = assertTrustedUrl(endpoint, this.config.apiUrl);

    return this.http
      .get<IIepSectionContributor[] | IIepContributor[]>(url)
      .pipe(
        map((data: IIepSectionContributor[] | IIepContributor[]) => {
          if (
            Array.isArray(data) &&
            data.length > 0 &&
            'contributorLst' in (data[0] as IIepSectionContributor)
          ) {
            return (data as IIepSectionContributor[])
              .flatMap((section: IIepSectionContributor) =>
                section?.contributorLst ?? []
              )
              .map((contributor: IIepContributor) => ({
                ...contributor,
                personSk:
                  (contributor as unknown as { personSk?: string; personSK?: string })
                    ?.personSk ??
                  (contributor as unknown as { personSk?: string; personSK?: string })
                    ?.personSK,
              }));
          }

          return ((data as IIepContributor[]) ?? []).map(
            (contributor: IIepContributor) => ({
              ...contributor,
              personSk:
                (contributor as unknown as { personSk?: string; personSK?: string })
                  ?.personSk ??
                (contributor as unknown as { personSk?: string; personSK?: string })
                  ?.personSK,
            })
          );
        }),
        catchError(handleError)
      );
  }

  public isLocked(lockRequest: ILockRequest): Observable<ILock> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Contributor/GetLock/${encodeURIComponent(lockRequest.iepFormSk)}/${encodeURIComponent(lockRequest.sectionID)}/${encodeURIComponent(lockRequest.userName)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ILock>(url)
      .pipe(catchError(handleError));
  }

  public releaseLock(lockRequest: ILockRequest): Observable<number> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Contributor/ReleaseLock/${encodeURIComponent(lockRequest.iepFormSk)}/${encodeURIComponent(lockRequest.sectionID)}/${encodeURIComponent(lockRequest.userName)}`,
      this.config.apiUrl
    );
    return this.http
      .get<number>(url)
      .pipe(catchError(handleError));
  }


  public get(id: string, type: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/Get/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http.get<any[]>(url).pipe(

      map((res: any) => res?.value || res),
      catchError(handleError)
    );
  }

  public getWithFlag(
    id: string,
    type: string,
    flagName: string,
    flag: boolean
  ): Observable<AddProgramServicesRequest> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/Get/${encodeURIComponent(id)}?${encodeURIComponent(flagName)}=${flag}`,
      this.config.apiUrl
    );
    return this.http
      .get<AddProgramServicesRequest>(url)
      .pipe(catchError(handleError));
  }

  public getMeetingDetails(
    id: string,
    type: string
  ): Observable<IMeetingDetails> {
    const url = assertTrustedUrl(
      `${this.config.studentApiUrl}/${encodeURIComponent(type)}/Get/${encodeURIComponent(id)}`,
      this.config.studentApiUrl
    );
    return this.http
      .get<IMeetingDetails>(url)
      .pipe(catchError(handleError));
  }

  public getDetails(
    id: string,
    type: string,
    formtype: number
  ): Observable<ApiResponse<CBSTRequest>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/Get/${encodeURIComponent(id)}/${encodeURIComponent(String(formtype))}`,
      this.config.apiUrl
    );
    return this.http
      .get<ApiResponse<CBSTRequest>>(url)
      .pipe(catchError(handleError));
  }
  public getLreandAccuranceDetails(
    id: string,
    type: string,
    formtype: number,
    rejectedPplacementType?:boolean | null
  ): Observable<CBSTRequest> {
  const baseUrl = `${this.config.apiUrl}/CBST/GetCentralPackageDetails/${encodeURIComponent(id)}/${encodeURIComponent(
      String(formtype)
    )}`;
    const url = assertTrustedUrl(
      rejectedPplacementType !== undefined && rejectedPplacementType !== null
        ? `${baseUrl}/${encodeURIComponent(String(rejectedPplacementType))}`
        : baseUrl,
      this.config.apiUrl
    );
    return this.http
      .get<CBSTRequest>(url)
      .pipe(catchError(handleError));
  }
  public getAssessmentsForIEP(iepformsk: string,studentSk : string): Observable<Assessment[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/CBST/GetAssessments/${encodeURIComponent(iepformsk)}/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<Assessment[]>(url)
      .pipe(catchError(handleError));
  }

  public getProgramsAndServices(
    iepformsk: string
  ): Observable<IEPProgramServicesKeyValuePairModel> {
    const safeIepFormSk: string = this.assertSafeIdentifier(iepformsk);
    const url = this.trustedApiUrl(`/IEPProgramServices/GetProgramServices/${encodeURIComponent(safeIepFormSk)}`);
    return this.http
      .get<IEPProgramServicesKeyValuePairModel>(url)
      .pipe(catchError(handleError));
  }

  public getSEITApplicableForPAL(studentSk: string): Observable<boolean> {
    const params: HttpParams = new HttpParams().set('studentSk', studentSk);
    const url = this.trustedApiUrl('/IEPProgramServices/GetSEITApplicableForPAL/');
    return this.http
      .get<boolean>(url, { params })
      .pipe(catchError(handleError));

  }

  public getProgramsAndServicesByNycId(nycid: string): Observable<IEPResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/IEPProgramServices/GetStudentIEP/${encodeURIComponent(nycid)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IEPResponse>(url)
      .pipe(catchError(handleError));
  }

  getEligibility(iepSK: string): Observable<EligibilityResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/IEP/Get/${encodeURIComponent(iepSK)}`,
      this.config.apiUrl
    );
    return this.http.get<EligibilityResponse>(url);
  }

  public getCaptureFirstDateByNycId(
    nycid: string
  ): Observable<CapatureFirstAttendResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PlacementFinder/GetFirstAttendenceDetails?studentId=${encodeURIComponent(nycid)}`,
      this.config.apiUrl
    );
    return this.http
      .get<CapatureFirstAttendResponse>(url)
      .pipe(catchError(handleError));
  }

  public getIEPTeam(nycid: string, dbn: string): Observable<IepTeam> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/IEPProgramServices/getIEPTeam/${encodeURIComponent(nycid)}/${encodeURIComponent(dbn)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IepTeam>(url)
      .pipe(catchError(handleError));
  }

  public getMiscDetails(studentSk: string): Observable<StageActions[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Dashboard/GetMiscInfo/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<StageActions[]>(url)
      .pipe(catchError(handleError));
  }

  public getExistingAttendanceList(iepformsk: string): Observable<Attendees> {
    const url = this.trustedStudentApiUrl(
      `/Meeting/GetAttendees/${encodeURIComponent(iepformsk)}`
    );
    return this.http
      .get<Attendees>(url)
      .pipe(catchError(handleError));
  }

  public getMeeting(meetingsk: string): Observable<string> {
    const url = this.trustedStudentApiUrl(
      `/Meeting/Get/${encodeURIComponent(meetingsk)}`
    );
    return this.http
      .get<string>(url)
      .pipe(catchError(handleError));
  }

  public getComplianceDateForIep(
    id: string,
    isReferral: boolean
  ): Observable<Compliance> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/IEP/GetComplianceDate?iepFormsk=${encodeURIComponent(id)}&isReferral=${encodeURIComponent(String(isReferral))}`,
      this.config.apiUrl
    );
    return this.http
      .get<Compliance>(url)
      .pipe(catchError(handleError));
  }

  public getComplianceDateForcaseclose(
    id: string,
    isReferral: boolean
  ): Observable<Compliance> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/IEP/GetComplianceDateCaseClose?iepFormsk=${encodeURIComponent(id)}&isReferral=${encodeURIComponent(String(isReferral))}`,
      this.config.apiUrl
    );
    return this.http
      .get<Compliance>(url)
      .pipe(catchError(handleError));
  }

  public submitAttendanceDocument(
    id: string,
    request: Attendees
  ): Observable<Uint8Array> {
    const url = this.trustedApiUrl(
      `/DocumentGen/AttendanceDocument?meetingSk=${encodeURIComponent(id)}`
    );
    return this.http
      .post<Uint8Array>(url, request)
      .pipe(catchError(handleError));
  }

  public submitCaptureFirstAttendDate(
    request: FirstAttendDateRequest[]
  ): Observable<boolean> {
    const url = this.trustedApiUrl('/PlacementFinder/CaptureFirstAttendDate');
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  public getPastIEPs(studentSk: string): Observable<IepProgressReport[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/ProgressReport/GetIEPByStudent/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IepProgressReport[]>(url)
      .pipe(catchError(handleError));
  }

  public getProgressReport(
    progressReportSK: string
  ): Observable<ProgressReportDetail> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/ProgressReport/GetProgressReport/${encodeURIComponent(progressReportSK)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ProgressReportDetail>(url)
      .pipe(catchError(handleError));
  }

  getSpecialTransportationServiceDetails(
    iepFormSk: string
  ): Observable<ApiResponse<SpecialTransportationRequest>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/SpecialTransportation/Get/${encodeURIComponent(iepFormSk)}`,
      this.config.apiUrl
    );
    return this.http.get<ApiResponse<SpecialTransportationRequest>>(url);
  }

  public getGoalsForNewProgressReport(
    iepFormSk: string
  ): Observable<ProgressReportDetail> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/ProgressReport/GetGoalsForNewProgressReport/${encodeURIComponent(iepFormSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ProgressReportDetail>(url)
      .pipe(catchError(handleError));
  }

  public updateProgressReportGoal(
    goal: ProgressReportRequest
  ): Observable<ApiResponse<ProgressReportRequest>> {
    const url = this.trustedApiUrl('/ProgressReport/Add');
    return this.http
      .post<ApiResponse<ProgressReportRequest>>(url, goal)
      .pipe(catchError(handleError));
  }

  public getTransport(
    id: string
  ): Observable<ApiResponse<SpecialTransportationRequest>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/SpecialTransportation/Get/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ApiResponse<SpecialTransportationRequest>>(url)
      .pipe(catchError(handleError));
  }

  public getPreparerInformation(): Observable<PreparerInformation> {
    const url = this.trustedApiUrl('/CBST/PreparerDetailes');
    return this.http
      .get<PreparerInformation>(url)
      .pipe(catchError(handleError));
  }

  public getEducationalBenefitReviewProcess(
    id: string
  ): Observable<EducationalBenefitReviewResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/EducationalBenefitReviewProcess/Get/IEPForEdBenefitReview/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<EducationalBenefitReviewResponse>(url)
      .pipe(
        map((res: EducationalBenefitReviewResponse) => res),
        catchError(handleError)
      );
  }

  public getMeetingDate(
    iepFormSk: string
  ): Observable<ApiResponse<EligibilityRequest>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/IEPConsent/GetMeeting/${encodeURIComponent(iepFormSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ApiResponse<EligibilityRequest>>(url)
      .pipe(catchError(handleError));
  }

  public getCalculatedEndDate(
    request: ICalculatedEndDateRequest
  ): Observable<string> {
    const url = this.trustedApiUrl('/Evaluations/GetCalculatedEndDate');
    return this.http
      .post<string>(url, request)
      .pipe(catchError(handleError));
  }

  public printProgressReport(
    request: PrintProgressReportModel,
    iepFormSK: string,
    studentSk: string
  ): Observable<Uint8Array> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/DocumentGen/ProgressReport?Iepformsk=${encodeURIComponent(iepFormSK)}&StudentSk=${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .post<Uint8Array>(url, request)
      .pipe(catchError(handleError));
  }

  public generateFinalProgressReport(
    iepFormSK: string,
    studentSk: string,
    iepDate: string
  ): Observable<Uint8Array> {

     const params = new URLSearchParams();
    params.append('iepFormSK', iepFormSK);
    params.append('studentSk', studentSk);
   params.append('iepDate', iepDate);

    const url = assertTrustedUrl(
      `${this.config.apiUrl}/DocumentGen/ProgressReportCumulative?${params}`,
      this.config.apiUrl
    );
    return this.http
      .get<Uint8Array>(url)
      .pipe(catchError(handleError));
  }

  public previewDocument(iepFormSk: string): Observable<Uint8Array> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/DocumentGen/PreviewIEPDocument?Iepformsk=${encodeURIComponent(iepFormSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<Uint8Array>(url)
      .pipe(catchError(handleError));
  }

  public getPlannedAssessments(nycId: string): Observable<IAssessmentPlanning> {
    return this.studentService.studentDetailsById(nycId).pipe(

      take(1),
      switchMap((response: StudentDetails) => {
        return this.evaluationService.getAssessmentPlanning(
          response?.studentSk
        );
      })
    );
  }

  public getPreviousIepDetails(
    studentSk: string
  ): Observable<IepDetailsResponse> {
    const safeStudentSk: string = this.assertSafeIdentifier(studentSk);
    const url = this.trustedApiUrl(`/IEPProgramServices/GetPreviousiepDetails?studentSk=${encodeURIComponent(safeStudentSk)}`);
    return this.http
      .get<IepDetailsResponse>(url)
      .pipe(catchError(handleError));
  }

  public setEligibilityStatus(iepStatus: number): void {
    this.eligibilityStatusSubject.next(iepStatus);
  }

  public updateIepSave(iepSave: string): void {
    this.iepSave.next(iepSave);
  }

  public getIepSave(): Observable<string> {
    return this.iepSave.asObservable();
  }

  public submitUpdateFirstAttendDate(request: FirstAttendDateRequest): Observable<boolean> {
    const url = this.trustedApiUrl('/IEPProgramServices/InsertorUpdateFirstAttendDate');
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  public getProgressReports(iepFormSk: string): Observable<IepProgressReport[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/ProgressReport/GetProgressReportWithDate/${encodeURIComponent(iepFormSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IepProgressReport[]>(url)
      .pipe(catchError(handleError));
  }

  public getScheduleMeetingList(id: string): Observable<IMeetingDetails[]> {
    const url = this.trustedStudentApiUrl(
      `/Meeting/GetMeetingList/${encodeURIComponent(id)}`
    );
    return this.http
      .get<IMeetingDetails[]>(url)
      .pipe(catchError(handleError));
  }

  setSelectedDisability(disabilityId: number | null): void {
    this.selectedDisabilitySubject.next(disabilityId);
  }

  public onChangeIepType(request: IIepType): Observable<ApiResponse<IIepType>> {
    const url = this.trustedApiUrl('/IEP/OnchangeIEP');
    return this.http
      .post<ApiResponse<IIepType>>(url, request)
      .pipe(catchError(handleError));
  }
  public addIepTypeAsCSP(request: T): Observable<ApiResponse<T>> {
    const url = this.trustedApiUrl('/IEP/Add');
    return this.http
      .post<ApiResponse<T>>(url, request)
      .pipe(catchError(handleError));
  }

  public IEPMeasuableAnnualGoal(request: T): Observable<ApiResponse<T>> {
    const url = this.trustedApiUrl('/IEPMeasuableAnnualGoal/Add');
    return this.http
      .post<ApiResponse<T>>(url, request)
      .pipe(catchError(handleError));
  }

  public updateIepType(iepType: IIepType): void {
    this.iepType.next(iepType);
  }

  public updateIepMeeting(iepMeeting: IepMeeting): void {
    this.iepMeeting.next(iepMeeting);
  }

  public getIepType(): Observable<IIepType> {
    return this.iepType.asObservable();
  }
}
